"use client"

import Image from "next/image"
import { type CSSProperties, useEffect, useRef, useState } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import type { HomeTrendingBrand } from "@modules/home/types"

import { BLUR_LAYERS } from "./blur-layers"
import styles from "./trending-brands.module.css"

/** Soglie dell'osservatore: bastano per capire quale card è più in vista. */
const VISIBILITY_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1]

type Props = {
  items: HomeTrendingBrand[]
}

/**
 * Versione mobile: scroll nativo con aggancio (`scroll-snap`), una card per
 * gesto, senza loop. Nessuna libreria: il movimento è del browser, il JS
 * serve solo a sapere quale card è in focus.
 */
export default function TrendingBrandsMobile({ items }: Props) {
  const [active, setActive] = useState(0)
  const trackRef = useRef<HTMLUListElement>(null)
  const last = items.length - 1

  useEffect(() => {
    const track = trackRef.current
    if (!track || items.length < 2) {
      return
    }

    const ratios = new Map<Element, number>()
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => ratios.set(entry.target, entry.intersectionRatio))

        let best = -1
        let bestRatio = 0
        Array.from(track.children).forEach((slide, index) => {
          const ratio = ratios.get(slide) ?? 0
          if (ratio > bestRatio) {
            best = index
            bestRatio = ratio
          }
        })
        if (best >= 0) {
          setActive(best)
        }
      },
      { root: track, threshold: VISIBILITY_THRESHOLDS }
    )

    Array.from(track.children).forEach((slide) => observer.observe(slide))
    return () => observer.disconnect()
  }, [items.length])

  const goTo = (index: number) => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    trackRef.current?.children[index]?.scrollIntoView({
      behavior: reduced ? "auto" : "smooth",
      block: "nearest",
      inline: "start",
    })
  }

  return (
    <div
      className={styles.mobile}
      role="region"
      aria-roledescription="carosello"
      aria-label="Brand di tendenza"
    >
      <ul ref={trackRef} className={styles.track}>
        {items.map((item, index) => {
          const isActive = index === active

          return (
            <li
              key={item.brandHandle}
              className={styles.mobileSlide}
              data-active={isActive}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} di ${items.length}`}
            >
              <LocalizedClientLink
                href={`/shop/${encodeURIComponent(item.brandHandle)}`}
                className={styles.link}
                draggable={false}
              >
                <Image
                  src={item.image.url}
                  alt={item.alt || item.label}
                  fill
                  sizes="80vw"
                  draggable={false}
                  className={styles.image}
                />
                {BLUR_LAYERS.map(({ blur, from, to }) => (
                  <span
                    aria-hidden="true"
                    className={styles.blurLayer}
                    key={blur}
                    style={
                      {
                        "--tb-blur": `${blur}px`,
                        "--tb-from": `${from}%`,
                        "--tb-to": `${to}%`,
                      } as CSSProperties
                    }
                  />
                ))}
                <span className={styles.label} aria-hidden="true">
                  {item.label}
                </span>
              </LocalizedClientLink>
            </li>
          )
        })}
      </ul>

      {active > 0 && (
        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowPrev}`}
          aria-label="Brand precedente"
          onClick={() => goTo(active - 1)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m14.5 5-7 7 7 7" />
          </svg>
        </button>
      )}
      {active < last && (
        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowNext}`}
          aria-label="Brand successivo"
          onClick={() => goTo(active + 1)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m9.5 5 7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  )
}
